import { getStores, hasSDK } from '@littlebird/sdk';
import { useEffect } from 'react';

import { electronApis } from '@/features/electron-api';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { logError } from '@/lib/errorLogging';
import { userStore } from '@/stores/user.store';
import { inElectron } from '@/utils/capabilities';

import {
  attendeesFromParticipants,
  mergeAttendees,
} from './meetings-participants';
import { meetingStore } from './meetings.state';

/**
 * Saves the roster ContextKit reads from the meeting app as the recording
 * meeting's attendees. Calendar-linked meetings keep their calendar attendees.
 */
export function useMeetingParticipantSync() {
  const isFeatureEnabled = useFeature();
  const enabled = isFeatureEnabled(FEATURES.MEETING_PARTICIPANT_DETECTION);

  useEffect(() => {
    if (!enabled || !inElectron()) return;

    return electronApis.meetings.listenForMeetingParticipantsUpdated(
      ({ meetingId, participants, selfParticipant }) => {
        if (!hasSDK()) return;
        if (!meetingStore.isMeetingInRecordingLifecycle(meetingId)) return;

        const storeMeetingId = meetingId.toLowerCase();
        const meeting = getStores().meetings.get(storeMeetingId);
        if (!meeting || meeting.eventId) return;

        const attendees = mergeAttendees(
          meeting.attendees,
          attendeesFromParticipants(
            participants,
            selfParticipant,
            userStore.user?.name
          )
        );
        if (!attendees) return;

        getStores()
          .meetings.updateMeeting(storeMeetingId, { attendees })
          .catch((error) => {
            logError(
              error,
              '[meeting-participants] Failed to save participants'
            );
          });
      }
    );
  }, [enabled]);
}

export function useMeetingSpeakerNameSync() {
  const isFeatureEnabled = useFeature();
  const enabled = isFeatureEnabled(FEATURES.REALTIME_SPEAKER_ATTRIBUTION);

  useEffect(() => {
    if (!enabled || !inElectron()) return;

    return electronApis.meetings.listenForMeetingSpeakerNameResolved(
      ({ meetingId, diarizedLabel, speakerName }) => {
        meetingStore.setResolvedSpeakerName(
          meetingId.toLowerCase(),
          diarizedLabel,
          speakerName
        );
      }
    );
  }, [enabled]);
}
